import React, { useEffect, useMemo, useState, useRef } from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import debounce from 'lodash/debounce';
import Table from 'components/molecules/Table';
import Field from 'components/molecules/Field';
import Tooltip from 'components/atoms/Tooltip';
import ModalContainer from 'components/molecules/ModalContainer';
import Pagination from 'components/molecules/Pagination';
import commonService from 'services/commonService';
import { getDateObject } from 'helpers/common';
import Loaders from 'components/atoms/Loaders';
import {
  PayloadViewer,
  PayloadPre,
  FlowWrapper,
  FlowHeader,
  SummaryCard,
  SummaryGrid,
  SummaryItem,
  FlowTimeline,
  FlowStep,
  FlowStats,
  StatBadge,
  StepCard,
  StepHeader,
  StepHeaderLeft,
  StepLevelBadge,
  StepTimestamp,
  StepMessage,
  StepBodyWrapper,
  StepBody,
  StepToggle,
  EmptyState,
  InlineActions,
  IconButton,
  LogsContainer,
  LogsHeader,
  LogsTitle,
  FilterRow,
  FilterField,
  TableWrapper,
  PaginationWrapper,
} from './styles';

const columnNames = ['Time', 'Method', 'Type', 'Card Number', 'Amount', 'Message', ''];

const formatTimestamp = value => {
  if (!value) return '---';
  try {
    return format(getDateObject(value), 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return value || '---';
  }
};

// Common fields in all logs: deviceTxn, id, level, message, mid, serialNo, timestamp, transactionMethod
const COMMON_FIELDS = ['deviceTxn', 'id', 'level', 'message', 'mid', 'serialNo', 'timestamp', 'transactionMethod'];

const getStepPayload = log => {
  const payload = { ...log };
  COMMON_FIELDS.forEach(field => delete payload[field]);
  return Object.keys(payload).length ? payload : { note: 'No additional details' };
};

const stringifyPayload = payload => JSON.stringify(payload ?? {}, null, 2);

const getLevelColor = level => {
  const l = (level || '').toUpperCase();
  if (l === 'ERROR') return '#ef4444';
  if (l === 'WARN' || l === 'WARNING') return '#f59e0b';
  if (l === 'INFO') return '#3b82f6';
  if (l === 'DEBUG') return '#8b5cf6';
  return '#6b7280';
};

const getMessageSnippet = msg => {
  if (!msg) return '---';
  return msg.length > 50 ? `${msg.substring(0, 50)}...` : msg;
};

const calculateAmount = log => {
  if (log?.sale_amount !== undefined || log?.tip_amount !== undefined) {
    return (log.sale_amount || 0) + (log.tip_amount || 0);
  }
  if (log?.amount) {
    let { amount } = log;
    amount = String(amount);
    if (!String(amount).includes('.')) {
      if (amount.length <= 2) {
        amount = `0.${amount.padStart(2, '0')}`;
      } else {
        amount = `${amount.slice(0, -2)}.${amount.slice(-2)}`;
      }
    }
    return amount;
  }

  return null;
};

function DeviceOpenSearchLogs({ device }) {
  const serialNo = device?.serial_number ?? '';
  const [searchQuery, setSearchQuery] = useState({ page: 1, pageSize: 10 });
  const [filters, setFilters] = useState({ cardNumber: '', amount: '', rrn: '' });
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const debounceRef = useRef(0);

  // Only send dates when both are selected
  const hasCompleteDateRange = startDate && endDate;

  const { open_search_logs_data, open_search_logs_loading } = commonService.GetOpenSearchLogs({
    ...searchQuery,
    serialNo,
    cardNumber: filters.cardNumber,
    amount: filters?.amount,
    rrn: filters.rrn,
    startDate: hasCompleteDateRange ? startOfDay(startDate).toISOString() : null,
    endDate: hasCompleteDateRange ? endOfDay(endDate).toISOString() : null,
    all: false,
  });

  const items = open_search_logs_data?.items ?? [];
  const totalItems = open_search_logs_data?.totalItems ?? 0;
  const isLoading = open_search_logs_loading;

  const handleFilterChange = useMemo(
    () =>
      debounce((field, value) => {
        debounceRef.current += 1;
        const localRef = debounceRef.current;
        setTimeout(() => {
          if (localRef === debounceRef.current) {
            setFilters(prev => ({ ...prev, [field]: value }));
            setSearchQuery(prev => ({ ...prev, page: 1 }));
          }
        }, 1);
      }, 400),
    [],
  );

  const handleDateChange = ({ target: { value } }) => {
    setDateRange(value);
    // Only trigger search when both dates are selected or both are cleared
    if ((value[0] && value[1]) || (!value[0] && !value[1])) {
      setSearchQuery(prev => ({ ...prev, page: 1 }));
    }
  };

  const handlePageChange = page => {
    setSearchQuery(prev => ({ ...prev, page }));
  };

  const rowsData = useMemo(
    () =>
      items.map(log => {
        const amount = calculateAmount(log);
        return [
          <span style={{ fontSize: '12px', textTransform: 'capitalize' }}>
            {formatTimestamp(log.timestamp) || '---'}
          </span>,
          <span style={{ fontSize: '12px', textTransform: 'capitalize' }}>{log.transactionMethod || '---'}</span>,
          <span style={{ fontSize: '12px', textTransform: 'capitalize' }}>{log.transactionType || '---'}</span>,
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {log.cardNumber || log.card_number || '---'}
          </span>,
          amount ? `$${Number(amount).toFixed(2)}` : '---',
          <span title={log.message}>{getMessageSnippet(log.message)}</span>,
          <ActionButtons log={log} />,
        ];
      }),
    [items],
  );

  return (
    <LogsContainer>
      <LogsHeader>
        <LogsTitle>OpenSearch Logs</LogsTitle>
        <FilterRow>
          <FilterField>
            <Field
              type="text"
              placeholder="Card Number"
              label="Card Number"
              noMargin
              onChange={({ target: { value } }) => handleFilterChange('cardNumber', value.trim())}
              sm
            />
          </FilterField>
          <FilterField>
            <Field
              type="text"
              placeholder="Amount"
              label="Amount"
              noMargin
              onChange={({ target: { value } }) => handleFilterChange('amount', value.trim())}
              sm
            />
          </FilterField>
          <FilterField>
            <Field
              type="text"
              placeholder="RRN"
              label="RRN"
              noMargin
              onChange={({ target: { value } }) => handleFilterChange('rrn', value.trim())}
              sm
            />
          </FilterField>
          <FilterField $wide>
            <Field
              type="datepicker"
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              placeholderText="Select date range"
              label="Date Range"
              noMargin
              sm
              clear={startDate || endDate}
            />
          </FilterField>
        </FilterRow>
      </LogsHeader>
      <TableWrapper>
        <Table height={600} loading={isLoading} rowsData={rowsData} columnNames={columnNames} noPadding />
      </TableWrapper>
      <PaginationWrapper>
        <Pagination
          currentPage={searchQuery.page}
          totalCount={totalItems}
          pageSize={searchQuery.pageSize}
          onPageChange={handlePageChange}
        />
      </PaginationWrapper>
    </LogsContainer>
  );
}

function ActionButtons({ log }) {
  return (
    <InlineActions>
      <ModalContainer
        width={900}
        title="Log Payload"
        btnComponent={({ onClick }) => (
          <Tooltip title="View payload" type="dark" place="left">
            <IconButton type="button" onClick={onClick}>
              <i className="material-icons-outlined">code</i>
            </IconButton>
          </Tooltip>
        )}
        content={() => (
          <PayloadViewer>
            <PayloadPre>{stringifyPayload(log)}</PayloadPre>
          </PayloadViewer>
        )}
      />
      {log.deviceTxn ? (
        <ModalContainer
          width={1100}
          title="Device Flow"
          btnComponent={({ onClick }) => (
            <Tooltip title="View flow" type="dark" place="left">
              <IconButton type="button" onClick={onClick}>
                <i className="material-icons-outlined">timeline</i>
              </IconButton>
            </Tooltip>
          )}
          content={() => <DeviceFlowView log={log} />}
        />
      ) : (
        <Tooltip title="Flow view requires a deviceTxn value in the log" type="dark" place="left">
          <IconButton type="button" $disabled>
            <i className="material-icons-outlined">timeline</i>
          </IconButton>
        </Tooltip>
      )}
    </InlineActions>
  );
}
const getTimestampNumber = log => {
  const ts = log?.message?.timestamp ?? log?.timestamp;
  if (!ts) return 0;
  try {
    return new Date(ts).getTime();
  } catch {
    return 0;
  }
};

const buildSummary = (log, timeline) => {
  const timestamps = timeline.map(l => l.timestamp).filter(Boolean);
  const start = timestamps[0] ?? log.timestamp;
  const end = timestamps[timestamps.length - 1] ?? start;
  const eventsWithReferenceId = timeline.filter(l => l.referenceId);
  const lastEventWithReferenceId = eventsWithReferenceId[eventsWithReferenceId.length - 1];
  const rrn = lastEventWithReferenceId?.referenceId || log.rrn || '---';
  const eventsWithCardNumber = timeline.filter(l => l.cardNumber || l.card_number);
  const cardNumberEvent = eventsWithCardNumber[0];
  const cardNumber =
    log.cardNumber || log.card_number || cardNumberEvent?.cardNumber || cardNumberEvent?.card_number || '---';

  const logAmount = calculateAmount(log);
  const eventsWithAmount = timeline.filter(l => calculateAmount(l) !== null);
  const amountEvent = eventsWithAmount[0];
  const amount = logAmount ?? calculateAmount(amountEvent);

  const eventsWithMethod = timeline.filter(l => l.transactionMethod);
  const methodEvent = eventsWithMethod[0];
  const transactionMethod = log.transactionMethod || methodEvent?.transactionMethod || '---';

  const errorCount = timeline.filter(l => l.level?.toUpperCase() === 'ERROR').length;
  const warnCount = timeline.filter(l => ['WARN', 'WARNING'].includes(l.level?.toUpperCase())).length;

  let duration = '---';
  try {
    const diff = getDateObject(end).getTime() - getDateObject(start).getTime();
    if (diff < 1000) duration = `${diff}ms`;
    else if (diff < 60000) duration = `${(diff / 1000).toFixed(2)}s`;
    else duration = `${(diff / 60000).toFixed(2)}min`;
  } catch {
    duration = '---';
  }

  return {
    deviceTxn: log.deviceTxn || '---',
    rrn,
    serial: log.serialNo || '---',
    mid: log.mid || '---',
    cardNumber,
    amount: amount ? `$${Number(amount).toFixed(2)}` : '---',
    transactionMethod,
    start: formatTimestamp(start),
    end: formatTimestamp(end),
    totalSteps: timeline.length,
    errorCount,
    warnCount,
    hasErrors: errorCount > 0,
    duration,
  };
};

// Single timeline step component with expandable body
function TimelineStep({ entry, index, isLast, totalSteps }) {
  const [expanded, setExpanded] = useState(false);
  const level = (entry.level || 'INFO').toUpperCase();
  const levelColor = getLevelColor(level);

  const getLevelBgColor = lvl => {
    const l = lvl.toUpperCase();
    if (l === 'ERROR') return { bg: '#fef2f2', hover: '#fee2e2' };
    if (l === 'WARN' || l === 'WARNING') return { bg: '#fffbeb', hover: '#fef3c7' };
    if (l === 'INFO') return { bg: '#eff6ff', hover: '#dbeafe' };
    if (l === 'DEBUG') return { bg: '#f5f3ff', hover: '#ede9fe' };
    return { bg: '#f8fafc', hover: '#f1f5f9' };
  };

  const bgColors = getLevelBgColor(level);

  return (
    <FlowStep $stepNumber={index + 1} $levelColor={levelColor} $isLast={isLast}>
      <StepCard $levelColor={levelColor}>
        <StepHeader $levelBg={bgColors.bg} $levelBgHover={bgColors.hover} onClick={() => setExpanded(!expanded)}>
          <StepHeaderLeft>
            <StepLevelBadge $levelColor={levelColor}>{level}</StepLevelBadge>
            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
              Step {index + 1} of {totalSteps}
            </span>
          </StepHeaderLeft>
          <StepTimestamp>{formatTimestamp(entry.timestamp)}</StepTimestamp>
        </StepHeader>
        <StepMessage>{entry.message || '---'}</StepMessage>
        <StepToggle $expanded={expanded} onClick={() => setExpanded(!expanded)}>
          <i className="material-icons-outlined">expand_more</i>
          {expanded ? 'Hide Details' : 'Show Details'}
        </StepToggle>
        <StepBodyWrapper $expanded={expanded}>
          <StepBody>{stringifyPayload(getStepPayload(entry))}</StepBody>
        </StepBodyWrapper>
      </StepCard>
    </FlowStep>
  );
}

function DeviceFlowView({ log }) {
  const { deviceTxn } = log;
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(!!deviceTxn);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const fetchFlow = async () => {
      if (!deviceTxn) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        let sorted = [];
        const response = await commonService.searchOpenSearchLogs({
          deviceTxn,
          all: true,
          page: 1,
          pageSize: 10000,
        });
        if (!isMounted) return;

        sorted = [...(response.items ?? [])].sort((a, b) => getTimestampNumber(a) - getTimestampNumber(b));

        if (!isMounted) return;
        setTimeline(sorted);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message ?? 'Unable to load flow.');
        setTimeline([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchFlow();
    return () => {
      isMounted = false;
    };
  }, [deviceTxn]);

  const summary = useMemo(() => buildSummary(log, timeline), [log, timeline]);

  return (
    <FlowWrapper>
      <FlowHeader>Transaction Summary</FlowHeader>
      <SummaryCard>
        <SummaryGrid>
          <SummaryItem>
            <span>Card Number</span>
            <span>{summary.cardNumber}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Amount</span>
            <span>{summary.amount}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Method</span>
            <span style={{ textTransform: 'capitalize' }}>{summary.transactionMethod}</span>
          </SummaryItem>
          <SummaryItem>
            <span>RRN</span>
            <span>{summary.rrn}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Serial Number</span>
            <span>{summary.serial}</span>
          </SummaryItem>
          <SummaryItem>
            <span>MID</span>
            <span>{summary.mid}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Device Txn</span>
            <span>{summary.deviceTxn}</span>
          </SummaryItem>
          <SummaryItem>
            <span>Start Time</span>
            <span>{summary.start}</span>
          </SummaryItem>
          <SummaryItem>
            <span>End Time</span>
            <span>{summary.end}</span>
          </SummaryItem>
        </SummaryGrid>
      </SummaryCard>

      <FlowStats>
        <StatBadge $type="total">
          <i className="material-icons-outlined">format_list_numbered</i>
          {summary.totalSteps} Steps
        </StatBadge>
        <StatBadge $type="duration">
          <i className="material-icons-outlined">schedule</i>
          Duration: {summary.duration}
        </StatBadge>
        {summary.errorCount > 0 && (
          <StatBadge $type="error">
            <i className="material-icons-outlined">error</i>
            {summary.errorCount} Error{summary.errorCount > 1 ? 's' : ''}
          </StatBadge>
        )}
        {summary.warnCount > 0 && (
          <StatBadge $type="warning">
            <i className="material-icons-outlined">warning</i>
            {summary.warnCount} Warning{summary.warnCount > 1 ? 's' : ''}
          </StatBadge>
        )}
        {!summary.hasErrors && summary.totalSteps > 0 && (
          <StatBadge $type="success">
            <i className="material-icons-outlined">check_circle</i>
            No Errors
          </StatBadge>
        )}
      </FlowStats>

      <FlowHeader>Transaction Flow</FlowHeader>
      <Loaders loading={loading}>
        {error ? (
          <EmptyState>
            <i className="material-icons-outlined">error_outline</i>
            {error}
          </EmptyState>
        ) : timeline.length ? (
          <FlowTimeline>
            {timeline.map((entry, index) => (
              <TimelineStep
                key={entry.id ?? `${entry.timestamp}-${index}`}
                entry={entry}
                index={index}
                isLast={index === timeline.length - 1}
                totalSteps={timeline.length}
              />
            ))}
          </FlowTimeline>
        ) : (
          <EmptyState>
            <i className="material-icons-outlined">inbox</i>
            No logs found for this device transaction.
          </EmptyState>
        )}
      </Loaders>
    </FlowWrapper>
  );
}

export default DeviceOpenSearchLogs;
